import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  takeUntil,
} from 'rxjs';
import { AxiosError } from 'axios';
import { CountryProduct } from 'src/app/services/country-product/country-product.service';
import { CurrencyService } from 'src/app/services/currency/currency.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  LawFirmItem,
  LawFirmService,
} from 'src/app/services/law-firm/law-firm.service';
import {
  Fee,
  feeFeeTypes,
  feePaymentOccurrences,
  feeTypes,
  Product,
  ProductService,
  productTypes,
} from 'src/app/services/product-service/product-service.service';
import { Vat, VatService } from 'src/app/services/vat/vat.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropdownComponent } from '../../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { PayeeIconButtonComponent } from '../../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeToggleComponent } from '../../../../shared/payee-toggle/payee-toggle.component';

@Component({
  selector: 'app-edit-product-dialog',
  templateUrl: './edit-product-dialog.component.html',
  styleUrls: ['./edit-product-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    PayeeAutocompleteComponent,
    PayeeButtonComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeIconButtonComponent,
    PayeeToggleComponent,
    MatDialogClose,
  ],
})
export class EditProductDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<EditProductDialogComponent>>(MatDialogRef);
  private productService = inject(ProductService);
  private currencyService = inject(CurrencyService);
  private vatService = inject(VatService);
  private helpersService = inject(HelpersService);
  private lawFirmService = inject(LawFirmService);
  private snackbar = inject(MatSnackBar);

  private readonly data = inject<{
    product: Product | undefined;
    countryOptions: CountryProduct[];
  }>(MAT_DIALOG_DATA);

  private readonly destroy = new Subject<void>();
  readonly searchDefaultLawFirms$ = new Subject<string>();

  readonly product = this.data.product;
  readonly countryOptions = this.data.countryOptions ?? [];
  readonly formGroup = this.initFormGroup();

  vatOptions: Vat[] = [];
  currencyOptions: string[] = [];

  submitLoading = false;

  readonly lawFirmControl = this.fb.control<LawFirmItem | null>(null);
  defaultLawFirmOptions: LawFirmItem[] = [];
  readonly clientCountryControl = this.fb.control<CountryProduct | null>(null);
  readonly debtorCountryControl = this.fb.control<CountryProduct | null>(null);
  clientCountryOptions: CountryProduct[] = [];
  debtorCountryOptions: CountryProduct[] = [];
  readonly productTypeOptions: string[] = [...productTypes];

  readonly feeTypeOptions: string[] = [...feeTypes];
  readonly fee_type_percentage: Fee['type'] = 'percentage';
  readonly fee_type_unknown: Fee['type'] = 'unknown';

  readonly feePaymentOccurrenceOptions: string[] = [...feePaymentOccurrences];

  readonly feeFeeTypeOptions: string[] = [...feeFeeTypes];

  readonly euCountryIsos = [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
  ] as const;

  get name() {
    return this.formGroup.controls.name;
  }
  get type() {
    return this.formGroup.controls.type;
  }
  get client_country_isos() {
    return this.formGroup.controls.client_country_isos;
  }
  get debtor_country_isos() {
    return this.formGroup.controls.debtor_country_isos;
  }
  get fees() {
    return this.formGroup.controls.fees;
  }
  get defaultLawFirms() {
    return this.formGroup.controls.defaultLawFirms;
  }

  async ngOnInit(): Promise<void> {
    const [currencies, vats] = await Promise.all([
      this.currencyService.getCurrencies(),
      this.vatService.getVats(),
    ]);
    this.currencyOptions = currencies;
    this.vatOptions = vats;

    this.updateClientCountryOptions();
    this.updateDebtorCountryOptions();

    this.lawFirmControl.valueChanges
      .pipe(
        takeUntil(this.destroy),
        filter((v) => !!v),
      )
      .subscribe((v) => {
        this.addDefaultLawFirm(v);
        this.lawFirmControl.setValue(null);
        this.defaultLawFirmOptions = [];
      });

    this.clientCountryControl.valueChanges
      .pipe(
        takeUntil(this.destroy),
        filter((v): v is CountryProduct => !!v),
      )
      .subscribe((country) => {
        this.addClientCountry(country);
        this.clientCountryControl.setValue(null);
      });

    this.debtorCountryControl.valueChanges
      .pipe(
        takeUntil(this.destroy),
        filter((v): v is CountryProduct => !!v),
      )
      .subscribe((country) => {
        this.addDebtorCountry(country);
        this.debtorCountryControl.setValue(null);
      });

    this.searchDefaultLawFirms$
      .pipe(
        takeUntil(this.destroy),
        debounceTime(500),
        filter((v) => !!v && v.length > 2),
        distinctUntilChanged(),
      )
      .subscribe(async (search) => {
        try {
          this.defaultLawFirmOptions =
            await this.lawFirmService.listLawFirms(search);
          this.defaultLawFirmOptions = this.defaultLawFirmOptions.filter(
            (option) => {
              const control = this.defaultLawFirms.value.find(
                (lf) => lf?.id === option.id,
              );
              return !control;
            },
          );
        } catch (error) {
          console.error('Error while listing law firm options', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async submit(): Promise<void> {
    if (this.submitLoading || this.formGroup.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.fees);
      return;
    }

    const {
      name,
      type,
      client_country_isos,
      debtor_country_isos,
      fees,
      defaultLawFirms,
    } = this.formGroup.getRawValue();

    if (!name || !type) {
      return;
    }

    let client_country_isos_out: null | string[] = client_country_isos
      .map((v) => v?.iso)
      .filter((iso): iso is string => !!iso);
    if (!client_country_isos_out.length) {
      client_country_isos_out = null;
    }
    let debtor_country_isos_out: null | string[] = debtor_country_isos
      .map((v) => v?.iso)
      .filter((iso): iso is string => !!iso);
    if (!debtor_country_isos_out.length) {
      debtor_country_isos_out = null;
    }

    try {
      this.submitLoading = true;

      if (this.product) {
        await this.productService.updateProduct(this.product, {
          ...this.product,
          name,
          type,
          client_country_isos: client_country_isos_out,
          debtor_country_isos: debtor_country_isos_out,
          fees: fees as Fee[],
          defaultLawFirms: defaultLawFirms.map((v) => v!.id),
        });
        this.dialogRef.close({
          ...this.product,
          name,
          type,
          client_country_isos: client_country_isos_out,
          debtor_country_isos: debtor_country_isos_out,
          fees: fees as Fee[],
          defaultLawFirms,
        });
      } else {
        const result = await this.productService.createProduct({
          client_country_isos: client_country_isos_out,
          debtor_country_isos: debtor_country_isos_out,
          defaultLawFirms: defaultLawFirms.map((v) => v!.id),
          fees: fees as Fee[],
          is_active: false,
          name,
          type,
        } as Parameters<ProductService['createProduct']>[0]);
        this.dialogRef.close(result);
      }
    } catch (error: AxiosError | unknown) {
      console.error('Error while updating product price', error);
      let message = 'Valami hiba történt';
      if (error instanceof AxiosError && error.response?.data?.message) {
        message = error.response.data.message;
      }
      this.snackbar.open(message, 'OK');
    } finally {
      this.submitLoading = false;
    }
  }

  removeFee(i: number): void {
    this.fees.removeAt(i);
  }

  addFee(fee?: Fee): void {
    if (!fee && this.fees.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.fees);
      return;
    }

    this.fees.push(this.createFeeGroup(fee));
  }

  addClientCountry(country: CountryProduct): void {
    this.client_country_isos.push(this.fb.control(country));
    this.updateClientCountryOptions();
  }

  removeClientCountry(i: number): void {
    this.client_country_isos.removeAt(i);
    this.updateClientCountryOptions();
  }

  addDebtorCountry(country: CountryProduct): void {
    this.debtor_country_isos.push(this.fb.control(country));
    this.updateDebtorCountryOptions();
  }

  removeDebtorCountry(i: number): void {
    this.debtor_country_isos.removeAt(i);
    this.updateDebtorCountryOptions();
  }

  removeAllClientCountries(): void {
    this.client_country_isos.clear();
    this.updateClientCountryOptions();
  }

  removeAllDebtorCountries(): void {
    this.debtor_country_isos.clear();
    this.updateDebtorCountryOptions();
  }

  addAllEuClientCountries(): void {
    const selected = this.client_country_isos.value
      .map((v) => (typeof v === 'object' && v && 'iso' in v ? v.iso : null))
      .filter(Boolean) as string[];
    for (const iso of this.euCountryIsos) {
      if (selected.includes(iso)) continue;
      const country = this.findCountryByIso(iso);
      if (country) {
        this.addClientCountry(country);
        selected.push(iso);
      }
    }
  }

  addAllEuDebtorCountries(): void {
    const selected = this.debtor_country_isos.value
      .map((v) => (typeof v === 'object' && v && 'iso' in v ? v.iso : null))
      .filter(Boolean) as string[];
    for (const iso of this.euCountryIsos) {
      if (selected.includes(iso)) continue;
      const country = this.findCountryByIso(iso);
      if (country) {
        this.addDebtorCountry(country);
        selected.push(iso);
      }
    }
  }

  addDefaultLawFirm(lawFirm?: LawFirmItem): void {
    if (!lawFirm && this.defaultLawFirms.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.defaultLawFirms);
      return;
    }

    this.defaultLawFirms.push(
      this.fb.control<LawFirmItem | null>(lawFirm ?? null),
    );
  }

  dlfDisplayWith(lawFirm?: LawFirmItem): string {
    return lawFirm?.name ?? '';
  }

  readonly vatDisplayedWith = (vat?: number | Fee): string => {
    if (typeof vat === 'number') {
      return this.vatOptions.find((v) => v.id === vat)?.label ?? '';
    }
    return vat?.label ?? '';
  };

  countryDisplayWith(country?: CountryProduct): string {
    return country?.name ?? '';
  }

  currencyDisplayWith(currency?: string | null): string {
    return currency ?? '';
  }

  getFeePanelDescription(
    feeForm: ReturnType<typeof this.createFeeGroup>,
  ): string {
    const type = feeForm.controls.type.value;
    const fee = feeForm.controls.fee.value;
    const currency = feeForm.controls.currency_iso.value;
    if (type === 'percentage') {
      const parts: string[] = [];
      if (fee) parts.push(`${fee}%`);
    }
    const parts: string[] = [];
    if (fee) parts.push(String(fee));
    if (currency) parts.push(currency);
    return parts.join(' ');
  }

  private initFormGroup() {
    const clientIsos = this.product?.client_country_isos ?? [];
    const debtorIsos = this.product?.debtor_country_isos ?? [];

    const form = this.fb.group({
      name: this.fb.control(this.product?.name ?? null, [
        Validators.required,
        Validators.maxLength(255),
      ]),
      type: this.fb.control<Product['type'] | null>(
        this.product?.type ?? null,
        Validators.required,
      ),
      client_country_isos: this.fb.array(
        clientIsos
          .map((iso) => this.findCountryByIso(iso))
          .filter((c): c is CountryProduct => !!c)
          .map((c) => this.fb.control<CountryProduct | null>(c)),
      ),
      debtor_country_isos: this.fb.array(
        debtorIsos
          .map((iso) => this.findCountryByIso(iso))
          .filter((c): c is CountryProduct => !!c)
          .map((c) => this.fb.control<CountryProduct | null>(c)),
      ),
      fees: this.fb.array([] as ReturnType<typeof this.createFeeGroup>[]),
      defaultLawFirms: this.fb.array(
        [] as ReturnType<typeof this.fb.control<LawFirmItem | null>>[],
      ),
    });

    this.product?.fees.forEach((fee) =>
      form.controls.fees.push(this.createFeeGroup(fee)),
    );
    this.product?.defaultLawFirms.forEach((dlf) =>
      form.controls.defaultLawFirms.push(
        this.fb.control<LawFirmItem | null>(dlf),
      ),
    );

    return form;
  }

  private createFeeGroup(fee?: Fee) {
    const feeForm = this.fb.group({
      id: this.fb.control<number | null | undefined>(fee?.id),
      label: this.fb.control(fee?.label ?? null, [
        Validators.required,
        Validators.maxLength(255),
      ]),
      currency_iso: this.fb.control(fee?.currency_iso ?? null),
      fee: this.fb.control(fee?.fee ?? null, [Validators.required]),
      type: this.fb.control(fee?.type ?? null, [Validators.required]),
      is_net: this.fb.control(!!fee?.is_net, { nonNullable: true }),
      payment_occurrence: this.fb.control(fee?.payment_occurrence ?? null, [
        Validators.required,
      ]),
      fee_type: this.fb.control(fee?.fee_type ?? null, [Validators.required]),
      vat_id: this.fb.control<number | Vat | null>(fee?.vat_id ?? null, [
        Validators.required,
      ]),
      min: this.fb.control<number | null | undefined>(fee?.min),
      max: this.fb.control<number | null | undefined>(fee?.max),
    });

    const feeControl = feeForm.controls.fee;
    const minControl = feeForm.controls.min;
    const maxControl = feeForm.controls.max;
    const isNetControl = feeForm.controls.is_net;

    feeForm.controls.type.valueChanges
      .pipe(takeUntil(this.destroy), startWith(fee?.type))
      .subscribe((type) => {
        if (!type || type === 'percentage') {
          feeControl?.enable();
          minControl?.enable();
          maxControl?.enable();
          isNetControl?.enable();
        } else if (type === 'amount') {
          feeControl?.enable();
          minControl?.disable();
          maxControl?.disable();
          isNetControl?.enable();
        } else if (type === 'unknown') {
          feeControl?.disable();
          minControl?.disable();
          maxControl?.disable();
          isNetControl?.disable();
        }
      });

    feeForm.controls.vat_id.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((vat) => {
        if (vat && typeof vat === 'object' && 'id' in vat) {
          feeForm.patchValue({
            vat_id: vat.id,
          });
        }
      });

    return feeForm;
  }

  private findCountryByIso(iso?: string | null): CountryProduct | undefined {
    return iso ? this.countryOptions.find((c) => c.iso === iso) : undefined;
  }

  private updateClientCountryOptions(): void {
    const selected: string[] = this.client_country_isos.value
      .map((v) => v?.iso)
      .filter((iso): iso is string => !!iso);
    this.clientCountryOptions = this.countryOptions.filter(
      (c) => !selected.includes(c.iso),
    );
  }

  private updateDebtorCountryOptions(): void {
    const selected: string[] = this.debtor_country_isos.value
      .map((v) => v?.iso)
      .filter((iso): iso is string => !!iso);
    this.debtorCountryOptions = this.countryOptions.filter(
      (c) => !selected.includes(c.iso),
    );
  }
}
